/**
 * AdminFeedback — triage dashboard for the product team.
 *
 * Lists every submission with filters by status + category. Each row
 * expands to show the full body, status pills the admin can toggle, and
 * a reply box that emails the original submitter via the backend.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Inbox, Bug, Lightbulb, Heart, MessageSquare, Star, Send, Loader2,
  CheckCircle2, Clock, Filter, ChevronDown, ChevronRight,
} from 'lucide-react';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { useLang } from '@/context/LangContext';
import { api } from '@/lib/api';

const CATEGORY_META = {
  bug:    { icon: Bug,           colorClass: 'text-red-300 border-red-500/40 bg-red-500/10' },
  idea:   { icon: Lightbulb,     colorClass: 'text-amber-300 border-amber-400/40 bg-amber-400/10' },
  praise: { icon: Heart,         colorClass: 'text-emerald-300 border-emerald-400/40 bg-emerald-400/10' },
  other:  { icon: MessageSquare, colorClass: 'text-sky-300 border-sky-400/40 bg-sky-400/10' },
};

const STATUSES = ['new', 'triaged', 'done'];
const STATUS_COLOR = {
  new:     'bg-amber-500/15 text-amber-300 border-amber-400/40',
  triaged: 'bg-sky-500/15 text-sky-300 border-sky-400/40',
  done:    'bg-emerald-500/15 text-emerald-300 border-emerald-400/40',
};

export default function AdminFeedback() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (categoryFilter !== 'all') params.set('category', categoryFilter);
      const { data } = await api.get(`/feedback/list?${params}`);
      setItems(data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, categoryFilter]);

  const stats = useMemo(() => {
    return {
      total: items.length,
      new: items.filter((i) => i.status === 'new').length,
      triaged: items.filter((i) => i.status === 'triaged').length,
      done: items.filter((i) => i.status === 'done').length,
    };
  }, [items]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/feedback/${id}/status`, { status });
      setItems((arr) => arr.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success(t('afb_status_changed'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <BackToDashboard />
        <header className="mb-5">
          <div className="text-[11px] font-mono uppercase tracking-[0.32em] text-gold/80">{t('afb_eyebrow')}</div>
          <h1 className="text-3xl sm:text-4xl font-h text-white mt-1">{t('afb_title')}</h1>
          <p className="text-sm text-slate-400 mt-1">{t('afb_subtitle')}</p>
        </header>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
          {[
            { key: 'total',   label: t('afb_stat_total'),   value: stats.total,   icon: Inbox },
            { key: 'new',     label: t('afb_stat_new'),     value: stats.new,     icon: Clock, color: 'text-amber-300' },
            { key: 'triaged', label: t('afb_stat_triaged'), value: stats.triaged, icon: Filter, color: 'text-sky-300' },
            { key: 'done',    label: t('afb_stat_done'),    value: stats.done,    icon: CheckCircle2, color: 'text-emerald-300' },
          ].map((s) => {
            const I = s.icon;
            return (
              <div key={s.key} className="rounded-lg border border-white/10 bg-black/30 p-3">
                <div className="flex items-center gap-1.5">
                  <I className={`w-3.5 h-3.5 ${s.color || 'text-slate-400'}`} />
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{s.label}</div>
                </div>
                <div className="text-2xl font-h text-white tabular-nums mt-1">{s.value}</div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="flex items-center gap-1 bg-black/30 border border-white/10 rounded-md p-1">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 px-2">{t('afb_status')}</span>
            {['all', ...STATUSES].map((s) => (
              <button
                key={s}
                data-testid={`afb-filter-status-${s}`}
                onClick={() => setStatusFilter(s)}
                className={`text-[11px] font-mono px-2 py-1 rounded ${statusFilter === s ? 'bg-gold text-navy font-bold' : 'text-slate-300 hover:bg-white/5'}`}
              >
                {s === 'all' ? t('afb_all') : t(`afb_status_${s}`)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-black/30 border border-white/10 rounded-md p-1">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 px-2">{t('afb_category')}</span>
            {['all', 'bug', 'idea', 'praise', 'other'].map((c) => (
              <button
                key={c}
                data-testid={`afb-filter-cat-${c}`}
                onClick={() => setCategoryFilter(c)}
                className={`text-[11px] font-mono px-2 py-1 rounded ${categoryFilter === c ? 'bg-gold text-navy font-bold' : 'text-slate-300 hover:bg-white/5'}`}
              >
                {c === 'all' ? t('afb_all') : t(`fb_cat_${c}`)}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-white/10 bg-black/30 p-8 text-center text-sm text-slate-400">
            {t('afb_empty')}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <FeedbackRow
                key={item.id}
                item={item}
                expanded={expanded === item.id}
                onToggle={() => setExpanded(expanded === item.id ? null : item.id)}
                onSetStatus={(s) => setStatus(item.id, s)}
                onReplied={() => load()}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function FeedbackRow({ item, expanded, onToggle, onSetStatus, onReplied, t }) {
  const meta = CATEGORY_META[item.category] || CATEGORY_META.other;
  const Icon = meta.icon;
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const sendReply = async () => {
    if (reply.trim().length < 10) {
      toast.error(t('afb_reply_validation'));
      return;
    }
    setSending(true);
    try {
      await api.post(`/feedback/${item.id}/reply`, { message: reply.trim() });
      toast.success(t('afb_reply_sent').replace('{email}', item.user_email || ''));
      setReply('');
      onReplied?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('afb_reply_fail'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid={`afb-row-${item.id}`} className="rounded-lg border border-white/10 bg-black/30 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-white/5"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`w-9 h-9 rounded-md border flex items-center justify-center shrink-0 ${meta.colorClass}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="text-sm text-white truncate">{item.title}</div>
              {item.rating ? (
                <div className="flex items-center gap-0.5 shrink-0">
                  {[1,2,3,4,5].map((n) => (
                    <Star key={n} className={`w-3 h-3 ${n <= item.rating ? 'fill-gold text-gold' : 'text-slate-600'}`} />
                  ))}
                </div>
              ) : null}
            </div>
            <div className="text-[10px] font-mono text-slate-400 truncate mt-0.5">
              {item.user_name || item.user_email} · {item.user_plan || 'no plan'} · {new Date(item.created_at).toLocaleString()}
            </div>
          </div>
        </div>
        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border shrink-0 ${STATUS_COLOR[item.status] || STATUS_COLOR.new}`}>
          {t(`afb_status_${item.status || 'new'}`)}
        </span>
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-white/5 p-4 space-y-3 bg-black/20">
          <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{item.body}</div>
          {item.page && (
            <div className="text-[10px] font-mono text-slate-500">page: {item.page}</div>
          )}

          {/* Status pills */}
          <div className="flex items-center gap-2 pt-1">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{t('afb_set_status')}:</span>
            {STATUSES.map((s) => (
              <button
                key={s}
                data-testid={`afb-set-${item.id}-${s}`}
                onClick={() => onSetStatus(s)}
                className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded border ${item.status === s ? STATUS_COLOR[s] : 'border-white/10 text-slate-400 hover:border-white/20'}`}
              >
                {t(`afb_status_${s}`)}
              </button>
            ))}
          </div>

          {/* Replies history */}
          {(item.replies || []).length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-gold/80">{t('afb_replies')}</div>
              {item.replies.map((r) => (
                <div key={r.id} className="rounded bg-black/40 border-l-2 border-gold p-2 text-xs text-slate-200">
                  <div className="text-[10px] text-slate-500 mb-1">{r.by_name} · {new Date(r.created_at).toLocaleString()}</div>
                  <div className="whitespace-pre-wrap">{r.message}</div>
                </div>
              ))}
            </div>
          )}

          {/* Reply form */}
          <div className="pt-2 border-t border-white/5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-gold/80 mb-1.5">{t('afb_reply_label')}</div>
            <textarea
              data-testid={`afb-reply-input-${item.id}`}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={3}
              placeholder={t('afb_reply_placeholder')}
              className="w-full bg-black/40 border border-white/10 focus:border-gold/40 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none resize-y"
            />
            <div className="flex justify-end mt-2">
              <button
                data-testid={`afb-reply-send-${item.id}`}
                onClick={sendReply}
                disabled={sending}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gold text-navy text-xs font-bold uppercase tracking-widest hover:brightness-110 disabled:opacity-60"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {sending ? t('afb_sending') : t('afb_reply_send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
